import React, { useState } from 'react';
import { uid } from '../db';
import { Btn, Field, Grid, Input, Modal, ModalFooter, Select, Span, toast } from '../ui.jsx';
import INDIA_HOLIDAYS from '../utils/holidays';

function AcademicCalendar({ db, save }) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [evForm, setEvForm] = useState({ title: '', dt: '', type: 'academic', note: '' });
  const [selectedDay, setSelectedDay] = useState(null); // { d, evs }
  const ef = k => v => setEvForm(f => ({ ...f, [k]: v }));

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const MONTHS_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const WDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const today = now.getDate(), todayM = now.getMonth(), todayY = now.getFullYear();

  // Merge: built-in holidays + db.events + db.exams
  const allEvents = [
    ...INDIA_HOLIDAYS.map(h => ({ ...h, id: 'IH_' + h.dt + '_' + h.title.slice(0, 8) })),
    ...(db.events || []),
    ...db.exams.filter(e => e.dt).map(e => ({
      id: e.id, title: e.name + (e.su ? ' – ' + e.su : ''), dt: e.dt, type: 'exam', cls: e.cls,
      note: e.cls ? 'Class: ' + e.cls : ''
    })),
  ];

  const saveEvent = () => {
    if (!evForm.title?.trim() || !evForm.dt) { toast('Title and date required', 'err'); return; }
    save({ ...db, events: [...(db.events || []), { id: 'EV' + uid(), ...evForm }] });
    setEvForm({ title: '', dt: '', type: 'academic', note: '' });
    setShowAddEvent(false);
    toast('Event added');
  };
  const delEvent = id => { save({ ...db, events: (db.events || []).filter(e => e.id !== id) }); };

  // Grid
  const offset = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();

  const monthEvents = allEvents.filter(e => {
    const d = new Date(e.dt);
    return d.getFullYear() === viewYear && d.getMonth() === viewMonth;
  });
  const eventsByDay = {};
  monthEvents.forEach(e => {
    const d = new Date(e.dt).getDate();
    if (!eventsByDay[d]) eventsByDay[d] = [];
    eventsByDay[d].push(e);
  });

  const upcoming = allEvents
    .filter(e => new Date(e.dt) >= new Date(now.toDateString()))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt))
    .slice(0, 8);

  const isToday = d => d === today && viewMonth === todayM && viewYear === todayY;

  const typeStyle = type => {
    if (type === 'exam') return { bg: 'bg-error-container/30', text: 'text-on-error-container', dot: 'bg-error', label: 'Examination', icon: 'quiz' };
    if (type === 'holiday') return { bg: 'bg-[#ffdeaa]/50', text: 'text-[#5f4100]', dot: 'bg-[#f8bc4b]', label: 'Holiday', icon: 'celebration' };
    if (type === 'event') return { bg: 'bg-secondary-container/20', text: 'text-on-secondary-container', dot: 'bg-secondary', label: 'Event', icon: 'event' };
    return { bg: 'bg-primary/5', text: 'text-primary', dot: 'bg-primary', label: 'Academic', icon: 'school' };
  };

  const fmtDate = dt => {
    const d = new Date(dt);
    return { day: d.getDate(), mon: MONTHS_S[d.getMonth()], dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] };
  };

  const openDay = (d, evs) => {
    const dtStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    setEvForm(f => ({ ...f, dt: dtStr }));
    setSelectedDay({ d, evs, dtStr });
  };


  return (
    <div className="space-y-0 -m-6">
      {/* Header */}
      <div className="px-10 pt-10 pb-8 flex justify-between items-end flex-wrap gap-4">
        <div>
          <p className="text-primary font-bold tracking-[0.15em] text-xs uppercase mb-2">Institutional Planning</p>
          <h1 className="text-5xl font-extrabold text-on-surface tracking-tight leading-none mb-2 font-headline">{MONTHS[viewMonth]} {viewYear}</h1>
          <p className="text-on-surface-variant text-base">Academic events, examinations, and public holidays at a glance.</p>
        </div>
        <div className="flex flex-col gap-3 items-end">
          {/* Year stepper */}
          <div className="flex items-center gap-2 bg-surface-container rounded-xl p-1 shadow-sm">
            <button onClick={() => setViewYear(y => y - 1)} className="px-3 py-2 rounded-lg text-slate-500 hover:text-primary hover:bg-surface-container-lowest transition-all border-0 bg-transparent cursor-pointer">
              <span className="material-symbols-outlined text-[18px]">chevron_left</span>
            </button>
            <span className="px-3 text-sm font-black text-primary min-w-[52px] text-center">{viewYear}</span>
            <button onClick={() => setViewYear(y => y + 1)} className="px-3 py-2 rounded-lg text-slate-500 hover:text-primary hover:bg-surface-container-lowest transition-all border-0 bg-transparent cursor-pointer">
              <span className="material-symbols-outlined text-[18px]">chevron_right</span>
            </button>
          </div>
          {/* Month pills */}
          <div className="flex flex-wrap gap-1 justify-end max-w-sm">
            {MONTHS_S.map((m, i) => (
              <button key={i} onClick={() => setViewMonth(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border-0 cursor-pointer
                  ${viewMonth === i ? 'bg-primary text-white shadow-sm' : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-lowest hover:text-primary'}`}>
                {m}
              </button>
            ))}
            <button onClick={() => { setViewMonth(todayM); setViewYear(todayY); }}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-surface-container-high text-primary hover:bg-surface-container-lowest transition-all border-0 cursor-pointer">
              Today
            </button>
          </div>
          <button onClick={() => { setEvForm({ title: '', dt: '', type: 'academic', note: '' }); setShowAddEvent(true); }}
            className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:opacity-90 transition-all border-0 cursor-pointer">
            <span className="material-symbols-outlined text-[16px]">add</span>
            New Event
          </button>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="px-10 pb-10 grid grid-cols-12 gap-8">

        {/* Main Calendar */}
        <div className="col-span-12 xl:col-span-9 bg-surface-container-low rounded-3xl p-8 shadow-sm">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-4">
            {WDAYS.map(d => (
              <div key={d} className="text-center text-[10px] font-black tracking-widest text-on-surface-variant uppercase py-1">{d}</div>
            ))}
          </div>
          {/* Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Prev month filler */}
            {Array.from({ length: offset }).map((_, i) => (
              <div key={'p' + i} className="aspect-square p-2 rounded-2xl opacity-20 bg-surface-container-high/50 flex flex-col">
                <span className="text-xs font-bold text-slate-400">{daysInPrev - offset + i + 1}</span>
              </div>
            ))}
            {/* Current month */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const d = i + 1;
              const evs = eventsByDay[d] || [];
              const isWknd = [0, 6].includes(new Date(viewYear, viewMonth, d).getDay());
              const todayCell = isToday(d);
              const domEv = evs[0];
              const ts = domEv ? typeStyle(domEv.type) : null;
              return (
                <div key={d}
                  onClick={() => openDay(d, evs)}
                  className={`aspect-square p-2.5 rounded-2xl flex flex-col justify-between cursor-pointer transition-all hover:scale-[1.03] hover:shadow-md
                    ${todayCell ? 'bg-primary-container ring-4 ring-primary/15 shadow-lg' :
                      ts ? ts.bg :
                        isWknd ? 'bg-surface-container' :
                          'bg-surface-container-lowest shadow-sm hover:ring-2 hover:ring-primary/10'}`}>
                  <span className={`text-sm font-black leading-none
                    ${todayCell ? 'text-white' : ts ? ts.text : isWknd ? 'text-slate-400' : 'text-on-surface'}`}>
                    {String(d).padStart(2, '0')}
                  </span>
                  <div className="space-y-0.5">
                    {todayCell && <span className="block text-[8px] font-bold uppercase tracking-widest text-white/70">Today</span>}
                    {evs.slice(0, 1).map(ev => (
                      <span key={ev.id} className={`block text-[8px] font-bold leading-tight truncate ${todayCell ? 'text-white/90' : ts?.text}`}>
                        {ev.title}
                      </span>
                    ))}
                    {evs.length > 1 && (
                      <span className={`block text-[8px] font-bold ${todayCell ? 'text-white/60' : ts?.text + ' opacity-60'}`}>
                        +{evs.length - 1} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="col-span-12 xl:col-span-3 flex flex-col gap-5">

          {/* Day Detail Panel — shown when a date is clicked */}
          {selectedDay ? (
            <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-sm border-l-4 border-primary">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant">{MONTHS[viewMonth]} {viewYear}</p>
                  <p className="text-3xl font-extrabold text-primary font-headline">{String(selectedDay.d).padStart(2, '0')}</p>
                  <p className="text-xs text-on-surface-variant font-medium">
                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date(viewYear, viewMonth, selectedDay.d).getDay()]}
                  </p>
                </div>
                <button onClick={() => setSelectedDay(null)} className="p-1.5 rounded-lg hover:bg-surface-container text-on-surface-variant transition-colors border-0 bg-transparent cursor-pointer">
                  <span className="material-symbols-outlined text-[18px]">close</span>
                </button>
              </div>
              {selectedDay.evs.length ? (
                <div className="space-y-3">
                  {selectedDay.evs.map(ev => {
                    const ts = typeStyle(ev.type);
                    return (
                      <div key={ev.id} className={`p-3 rounded-xl ${ts.bg}`}>
                        <div className="flex items-start gap-2">
                          <span className={`material-symbols-outlined text-[16px] mt-0.5 ${ts.text}`}>{ts.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold leading-tight ${ts.text}`}>{ev.title}</p>
                            {ev.note && <p className="text-[10px] text-on-surface-variant mt-0.5">{ev.note}</p>}
                            {ev.cls && <p className="text-[10px] text-on-surface-variant">Class: {ev.cls}</p>}
                            <span className={`inline-block mt-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${ts.bg} ${ts.text} border border-current/20`}>{ts.label}</span>
                          </div>
                          {!ev.id?.startsWith('IH_') && ev.type !== 'exam' && (
                            <button onClick={() => { delEvent(ev.id); setSelectedDay(s => ({ ...s, evs: s.evs.filter(e => e.id !== ev.id) })); }}
                              className="p-1 rounded hover:bg-error-container text-on-surface-variant hover:text-on-error-container transition-colors border-0 bg-transparent cursor-pointer flex-shrink-0">
                              <span className="material-symbols-outlined text-[14px]">delete</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <span className="material-symbols-outlined text-2xl text-on-surface-variant opacity-30 block mb-1">event_available</span>
                  <p className="text-xs text-on-surface-variant">No events on this day</p>
                </div>
              )}
              <button onClick={() => { setEvForm(f => ({ ...f, dt: selectedDay.dtStr })); setShowAddEvent(true); }}
                className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 bg-primary/10 text-primary rounded-xl text-xs font-bold hover:bg-primary/20 transition-colors border-0 cursor-pointer">
                <span className="material-symbols-outlined text-[14px]">add</span>
                Add event on this day
              </button>
            </div>
          ) : (
            /* Legend — shown when no day selected */
            <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-sm">
              <h3 className="text-xs font-black text-primary uppercase tracking-widest mb-5">Calendar Key</h3>
              <div className="space-y-3 mb-5">
                {[{ type: 'academic', label: 'Academic Events' }, { type: 'exam', label: 'Examinations' }, { type: 'holiday', label: 'Public Holidays' }, { type: 'event', label: 'Events & Festivals' }].map(({ type, label }) => {
                  const ts = typeStyle(type);
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full ${ts.bg} border-2 ${ts.text} border-current`}></div>
                      <span className="text-sm font-medium text-on-surface">{label}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-surface-container-high pt-4">
                <div className="flex items-center gap-1.5 text-[10px] text-on-surface-variant mb-1">
                  <span className="material-symbols-outlined text-[12px]">touch_app</span>
                  Click any date to see details
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-on-surface-variant">
                  <span className="material-symbols-outlined text-[12px]">public</span>
                  Indian public holidays included
                </div>
              </div>
            </div>
          )}

          {/* This Month summary */}
          <div className="bg-surface-container-lowest rounded-3xl p-6 shadow-sm">
            <h3 className="text-xs font-black text-primary uppercase tracking-widest mb-3">This Month</h3>
            <div className="space-y-2">
              {['holiday', 'exam', 'event', 'academic'].map(t => {
                const cnt = monthEvents.filter(e => e.type === t).length;
                if (!cnt) return null;
                const ts = typeStyle(t);
                return (
                  <div key={t} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${ts.dot}`}></div>
                      <span className="text-on-surface-variant">{ts.label}</span>
                    </div>
                    <span className="font-bold text-primary">{cnt}</span>
                  </div>
                );
              })}
              {!monthEvents.length && <p className="text-xs text-on-surface-variant">No events this month</p>}
            </div>
          </div>

          {/* Upcoming */}
          <div className="flex-1">
            <h3 className="text-lg font-extrabold font-headline text-on-surface mb-4">Upcoming</h3>
            <div className="space-y-3">
              {upcoming.map(ev => {
                const { day, mon, dow } = fmtDate(ev.dt);
                const ts = typeStyle(ev.type);
                return (
                  <div key={ev.id + ev.dt}
                    onClick={() => { const d = new Date(ev.dt); setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); openDay(d.getDate(), allEvents.filter(e => e.dt === ev.dt)); }}
                    className="bg-surface-container-lowest p-4 rounded-2xl flex gap-3 items-start shadow-sm hover:translate-x-1 transition-transform cursor-pointer group">
                    <div className={`flex flex-col items-center justify-center min-w-[46px] py-2 rounded-xl bg-surface-container-low group-hover:${ts.bg} transition-colors`}>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{mon}</span>
                      <span className="text-xl font-black text-primary leading-none">{day}</span>
                      <span className="text-[9px] text-slate-400 font-medium">{dow}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-on-surface leading-tight mb-1 truncate">{ev.title}</p>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${ts.dot}`}></div>
                        <span className="text-[10px] text-on-surface-variant font-medium">{ts.label}{ev.cls ? ' • ' + ev.cls : ''}</span>
                      </div>
                      {ev.note && <p className="text-[10px] text-on-surface-variant mt-0.5 truncate">{ev.note}</p>}
                    </div>
                  </div>
                );
              })}
              {!upcoming.length && (
                <div className="text-center py-8 text-on-surface-variant">
                  <span className="material-symbols-outlined text-3xl block mb-2 opacity-30">event_available</span>
                  <p className="text-xs font-medium">No upcoming events</p>
                </div>
              )}
            </div>

            {/* Add Event CTA */}
            <div className="mt-6 p-6 bg-gradient-to-br from-primary to-primary-container rounded-3xl text-white relative overflow-hidden cursor-pointer group"
              onClick={() => { setEvForm({ title: '', dt: '', type: 'academic', note: '' }); setShowAddEvent(true); }}>
              <div className="absolute -right-4 -bottom-4 opacity-10 rotate-12">
                <span className="material-symbols-outlined text-[100px]">event</span>
              </div>
              <h4 className="text-base font-bold mb-1 relative z-10">Add Custom Event</h4>
              <p className="text-xs opacity-75 mb-4 relative z-10 leading-relaxed">Schedule holidays, parent meetings, or academic milestones.</p>
              <button className="bg-white text-primary px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider shadow-lg hover:scale-105 transition-transform relative z-10 border-0 cursor-pointer">
                Launch Scheduler
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      <Modal open={showAddEvent} onClose={() => setShowAddEvent(false)} title="Add Academic Event">
        <Grid>
          <Span><Field label="Event Title *"><Input value={evForm.title} onChange={ef('title')} placeholder="Enter event title" /></Field></Span>
          <Field label="Date *"><Input type="date" value={evForm.dt} onChange={ef('dt')} /></Field>
          <Field label="Type">
            <Select value={evForm.type} onChange={ef('type')}>
              <option value="academic">Academic</option>
              <option value="exam">Examination</option>
              <option value="holiday">Holiday</option>
              <option value="event">Event / Festival</option>
            </Select>
          </Field>
          <Span><Field label="Note"><Input value={evForm.note} onChange={ef('note')} placeholder="Optional details..." /></Field></Span>
        </Grid>
        <ModalFooter>
          <Btn onClick={() => setShowAddEvent(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveEvent}>Add Event</Btn>
        </ModalFooter>
      </Modal>
    </div>
  );
}



export default AcademicCalendar;
